import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ChevronLeft, ChevronRight, Copy, Plus, Send, Undo2,
  Pencil, Trash2, Layers, AlertTriangle, Share2, FileImage, FileText, BellRing,
  Ship, PartyPopper,
} from "lucide-react";
import { toPng } from "html-to-image";
import { MANAGER_SITE_LABEL } from "@/components/ManagerLayout";
import { useConfirm } from "@/components/useConfirm";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Events and cruise ships in town on one rostered day. */
function DayNotice({ notice, iso }) {
  if (!notice) return null;
  const cruise = notice.cruise || [];
  const events = notice.events || [];
  if (!cruise.length && !events.length) return null;
  const pax = notice.pax || 0;
  const busy = pax >= 2000;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`mt-1.5 w-full rounded-md px-1.5 py-1 text-left leading-tight transition-colors ${
            busy
              ? "bg-[#C9A84C]/20 hover:bg-[#C9A84C]/30 text-[#7A5E12]"
              : "bg-gray-100 hover:bg-gray-200 text-gray-600"
          }`}
          data-testid={`day-notice-${iso}`}
          title="What's on in town"
        >
          <span className="flex items-center gap-1 text-[10px] font-semibold">
            {cruise.length > 0 && (
              <span className="flex items-center gap-0.5">
                <Ship className="h-3 w-3" />{cruise.length}
              </span>
            )}
            {events.length > 0 && (
              <span className="flex items-center gap-0.5">
                <PartyPopper className="h-3 w-3" />{events.length}
              </span>
            )}
            {pax > 0 && <span className="ml-auto">{pax.toLocaleString()}</span>}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start" data-testid={`day-notice-detail-${iso}`}>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">
          In town this day
        </div>
        <ul className="space-y-2">
          {cruise.map((c) => (
            <li key={c.id} className="flex gap-2 text-xs">
              <Ship className="h-3.5 w-3.5 text-[#1A2744] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-[#1A2744] truncate">{c.title}</div>
                <div className="text-gray-500">
                  {c.pax ? `${c.pax.toLocaleString()} guests` : "Guests TBC"}
                  {c.start_time ? ` · in ${c.start_time}` : ""}
                  {c.end_time ? ` · out ${c.end_time}` : ""}
                </div>
              </div>
            </li>
          ))}
          {events.map((e) => (
            <li key={e.id} className="flex gap-2 text-xs">
              <PartyPopper className="h-3.5 w-3.5 text-[#C9A84C] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-[#1A2744]">{e.title}</div>
                <div className="text-gray-500">
                  {e.venue}
                  {e.pax ? `${e.venue ? " · " : ""}${e.pax.toLocaleString()} expected` : ""}
                  {e.start_time ? ` · ${e.start_time}` : ""}
                </div>
                {e.notes ? <div className="text-gray-400 mt-0.5">{e.notes}</div> : null}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[10px] text-gray-400 mt-3">
          Numbers are estimates from published schedules and can change.
        </p>
      </PopoverContent>
    </Popover>
  );
}
const POSITION_LABEL = {
  BARISTA: "Barista",
  KITCHEN: "Kitchen",
  FRONT: "Front of House",
  ALL_ROUNDER: "All-Rounder",
  OTHER: "Other",
};

function mondayOf(iso) {
  const d = new Date(iso + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d;
}
function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtRange(mondayIso) {
  const a = new Date(mondayIso + "T00:00:00");
  const b = new Date(a); b.setDate(a.getDate() + 6);
  const opts = { day: "numeric", month: "short" };
  return `${a.toLocaleDateString([], opts)} — ${b.toLocaleDateString([], { ...opts, year: "numeric" })}`;
}
function fmtDuration(start, end) {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h${m}m` : `${h}h`;
}

function ShiftDialog({ trigger, data, positions, employees, defaultDate, onSave, onDelete }) {
  const isEdit = !!data;
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState(data?.user_id || "__unassigned__");
  const [shiftDate, setShiftDate] = useState(data?.date || defaultDate || "");
  const [start, setStart] = useState(data?.start_time || "09:00");
  const [end, setEnd] = useState(data?.end_time || "17:00");
  const [position, setPosition] = useState(data?.position || "BARISTA");
  const [note, setNote] = useState(data?.note || "");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setUserId(data?.user_id || "__unassigned__");
    setShiftDate(data?.date || defaultDate || "");
    setStart(data?.start_time || "09:00");
    setEnd(data?.end_time || "17:00");
    setPosition(data?.position || "BARISTA");
    setNote(data?.note || "");
  };

  const selected = employees.find((e) => e.id === userId);
  const overtime = selected && (selected.week_hours + (fmtDuration(start, end).includes("h") ? parseFloat(fmtDuration(start, end)) : 0)) > selected.contracted_weekly;
  const onLeave = selected?.leave_windows?.some((w) => shiftDate >= w.start_date && shiftDate <= w.end_date && w.status === "APPROVED");

  const submit = async () => {
    if (userId === "__unassigned__" && !isEdit) {
      toast.error("Pick an employee");
      return;
    }
    setBusy(true);
    try {
      await onSave({
        user_id: userId, date: shiftDate, start_time: start, end_time: end,
        position, note: note || null,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    const ok = await confirm({
      title: "Delete this shift?",
      description: "This removes the shift from the roster. Published rosters will notify the employee.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await onDelete();
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Edit shift" : "Add shift"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Employee</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="shift-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} · {e.week_hours}/{e.contracted_weekly}h
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label>End</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div className="text-xs text-gray-500">Duration: <span className="font-semibold text-[#1A2744]">{fmtDuration(start, end)}</span></div>
          <div>
            <Label>Position</Label>
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger data-testid="shift-position-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {positions.map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }}></span>
                      {POSITION_LABEL[p.code] || p.code}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Visible to employee" />
          </div>

          {onLeave && (
            <div className="p-2.5 rounded-lg bg-red-50 text-red-800 text-xs border border-red-200">
              ⚠ This employee has approved leave on {shiftDate}. You can proceed, but double-check.
            </div>
          )}
          {overtime && (
            <div className="p-2.5 rounded-lg bg-amber-50 text-amber-800 text-xs border border-amber-200">
              ⚠ Adding this pushes {selected?.name} over their contracted {selected?.contracted_weekly}h/week.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {isEdit && <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={doDelete}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>}
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesDialog({ currentWeek, onAppliedOrChanged }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/manager/roster/templates");
    setItems(data.templates);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/manager/roster/templates", { name, week_start: currentWeek });
      toast.success("Template saved");
      setName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const apply = async (id) => {
    try {
      const { data } = await api.post(`/manager/roster/templates/${id}/apply`, { week_start: currentWeek });
      toast.success(`Added ${data.added} shifts from template`);
      setOpen(false);
      onAppliedOrChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    const ok = await confirm({
      title: "Delete this template?",
      description: "This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/manager/roster/templates/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="roster-templates"><Layers className="h-3.5 w-3.5 mr-1" /> Templates</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Shift templates</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-xl border bg-gray-50">
            <Label>Save current week as template</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Week" data-testid="template-name-input" />
              <Button onClick={save} className="bg-[#1A2744] text-white">Save</Button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">Saves the pattern (times + positions) — employees get assigned when you apply.</p>
          </div>

          <div>
            <Label>Saved templates</Label>
            {items.length === 0 ? (
              <p className="text-xs text-gray-500 mt-2">No templates yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 mt-1">
                {items.map((t) => (
                  <li key={t.id} className="py-2 flex items-center justify-between gap-2" data-testid={`template-row-${t.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1A2744] truncate">{t.name}</div>
                      <div className="text-[11px] text-gray-500">{t.shifts_count} shifts{t.last_used_at ? ` · last used ${new Date(t.last_used_at).toLocaleDateString()}` : ""}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" className="bg-[#C9A84C] text-[#1A2744] hover:bg-[#C9A84C]/80" onClick={() => apply(t.id)}>Apply</Button>
                      <Button size="sm" variant="outline" className="text-red-700" onClick={() => del(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerRoster() {
  const [weekMonday, setWeekMonday] = useState(toIsoDate(mondayOf(toIsoDate(new Date()))));
  const [data, setData] = useState(null);
  const [notices, setNotices] = useState({});
  const [error, setError] = useState(null);
  const { site, scopeToken } = useManagerScope();
  const confirm = useConfirm();

  const load = useCallback(async (ws) => {
    setError(null);
    try {
      const { data } = await api.get("/manager/roster", { params: { week_start: ws } });
      setData(data);
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.detail || "Unable to load roster");
    }
  }, []);

  // What's on in town that week — events and cruise ships for this site.
  const noticeReq = useRef(0);
  const loadNotices = useCallback(async (ws, siteCode) => {
    const seq = ++noticeReq.current;
    const end = new Date(ws + "T00:00:00");
    end.setDate(end.getDate() + 6);
    try {
      const { data } = await api.get("/events/day-notices", {
        params: { start: ws, end: toIsoDate(end), ...(siteCode && siteCode !== "ALL" ? { site: siteCode } : {}) },
      });
      // Paging weeks quickly can land responses out of order — only the
      // newest request may paint.
      if (seq === noticeReq.current) setNotices(data.days || {});
    } catch {
      if (seq === noticeReq.current) setNotices({});
    }
  }, []);
  useEffect(() => {
    if (site === "ALL") { setData(null); setError(null); return; }
    load(weekMonday);
    loadNotices(weekMonday, site);
  }, [load, loadNotices, weekMonday, site, scopeToken]);

  const shiftsByCell = useMemo(() => {
    const m = {};
    if (!data) return m;
    for (const s of data.shifts) {
      const key = `${s.user_id}|${s.date}`;
      m[key] = m[key] || [];
      m[key].push(s);
    }
    return m;
  }, [data]);

  const moveWeek = (delta) => {
    const d = mondayOf(weekMonday);
    d.setDate(d.getDate() + delta * 7);
    setWeekMonday(toIsoDate(d));
  };

  // Hooks for the PDF/PNG share flow — must be declared BEFORE any early
  // return so the React-hooks rule of consistent ordering holds.
  const gridRef = useRef(null);
  const [pngCapture, setPngCapture] = useState(false);

  if (site === "ALL") {
    // Admin hasn't picked a site — render empty state (ManagerLayout already
    // shows an amber tip at the top of the page).
    return null;
  }
  if (error) {
    return <div className="text-sm text-red-600" data-testid="mgr-roster-error">{error}</div>;
  }
  if (!data) return <div className="text-sm text-gray-500">Loading roster…</div>;

  const isPublished = data.roster.status === "PUBLISHED";
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekMonday + "T00:00:00"); d.setDate(d.getDate() + i);
    return { iso: toIsoDate(d), label: DAYS[i], dom: d.getDate() };
  });

  const positionColor = (code) => data.positions.find((p) => p.code === code)?.color || "#6B7280";

  const addShift = async (payload) => {
    const { data: res } = await api.post("/manager/roster/shifts", payload);
    if (res.warnings?.overtime) toast.warning(`${payload.user_id.slice(0, 6)} is now over contracted weekly hours`);
    toast.success("Shift added");
    load(weekMonday);
  };
  const patchShift = async (id, payload) => {
    await api.patch(`/manager/roster/shifts/${id}`, payload);
    toast.success("Shift updated");
    load(weekMonday);
  };
  const deleteShift = async (id) => {
    await api.delete(`/manager/roster/shifts/${id}`);
    toast.success("Shift deleted");
    load(weekMonday);
  };
  const publish = async () => {
    const ok = await confirm({
      title: "Publish this week's roster?",
      description: `All rostered ${MANAGER_SITE_LABEL[data.site]} staff get an instant push notification and an email with their shifts.`,
      confirmText: "Publish",
    });
    if (!ok) return;
    const { data: res } = await api.post("/manager/roster/publish", { week_start: weekMonday });
    toast.success(`Roster published — ${res.notified ?? 0} staff notified`, {
      description: `${res.emailed ?? 0} roster email${res.emailed === 1 ? "" : "s"} sent`,
    });
    load(weekMonday);
  };
  const notifyChanges = async () => {
    const ok = await confirm({
      title: "Notify staff of roster changes?",
      description: "Everyone rostered this week gets a push notification and an email with their updated shifts.",
      confirmText: "Notify staff",
    });
    if (!ok) return;
    const { data: res } = await api.post("/manager/roster/publish", { week_start: weekMonday });
    toast.success(`${res.notified ?? 0} staff notified of the update`, {
      description: `${res.emailed ?? 0} email${res.emailed === 1 ? "" : "s"} sent`,
    });
    load(weekMonday);
  };
  const unpublish = async () => {
    const ok = await confirm({
      title: "Unpublish this roster?",
      description: "Staff will be notified to wait for an update.",
      confirmText: "Unpublish",
      destructive: true,
    });
    if (!ok) return;
    await api.post("/manager/roster/unpublish", { week_start: weekMonday });
    toast.success("Roster withdrawn");
    load(weekMonday);
  };
  const copyLast = async () => {
    try {
      const { data: res } = await api.post("/manager/roster/copy-last-week", { week_start: weekMonday });
      toast.success(`Copied ${res.copied} shifts from last week — review and publish`);
      load(weekMonday);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ===== Share roster (PDF + PNG) =========================================
  // Native `navigator.share({ files })` is the gold-standard on mobile — opens
  // the OS share-sheet (WhatsApp, iMessage, Telegram, Slack, etc). When not
  // available (most desktop browsers) we fall back to a plain download which
  // the user can drag into their channel of choice.
  const canShareFiles = (file) =>
    typeof navigator !== "undefined"
    && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [file] });

  const fileBase = `coffee-lovers-roster-${MANAGER_SITE_LABEL[data.site] || data.site}-${weekMonday}`
    .toLowerCase().replace(/\s+/g, "-");

  const offerShare = async (file, shareText) => {
    if (canShareFiles(file)) {
      try {
        await navigator.share({
          files: [file],
          title: "Coffee Lovers · Roster",
          text: shareText,
        });
        toast.success("Shared");
        return;
      } catch (err) {
        // User cancelled the share-sheet, or a 3rd-party app rejected it.
        // Don't toast an error — just fall through to the download fallback
        // so they can attach manually.
        if (err?.name !== "AbortError") {
          // log but still fall through
          console.warn("Web Share failed:", err);
        } else {
          return; // cancelled — don't double-download
        }
      }
    }
    // Fallback: trigger a download so the user can drag-attach.
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success(`${file.name} downloaded — drag it into your chat to share`);
  };

  const sharePdf = async () => {
    try {
      const res = await api.get(`/manager/rosters/${data.roster.id}/export.pdf`, { responseType: "blob" });
      const file = new File([res.data], `${fileBase}.pdf`, { type: "application/pdf" });
      await offerShare(file, `Roster for week of ${fmtRange(weekMonday)} · ${MANAGER_SITE_LABEL[data.site]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate PDF");
    }
  };

  const sharePng = async () => {
    if (!gridRef.current) { toast.error("Grid not ready"); return; }
    setPngCapture(true);
    // Wait a paint frame so the title strip is in the DOM before snapshotting.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const dataUrl = await toPng(gridRef.current, {
        backgroundColor: "#FFFFFF",
        pixelRatio: 2, // sharp on retina + when zoomed in social apps
        cacheBust: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `${fileBase}.png`, { type: "image/png" });
      await offerShare(file, `Roster for week of ${fmtRange(weekMonday)} · ${MANAGER_SITE_LABEL[data.site]}`);
    } catch (e) {
      toast.error("Couldn't snapshot the roster image");
    } finally {
      setPngCapture(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mgr-roster-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Roster</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">Build this week</h1>
          <p className="text-sm text-gray-500 mt-1">{fmtRange(weekMonday)} · {MANAGER_SITE_LABEL[data.site]}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={() => moveWeek(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeekMonday(toIsoDate(mondayOf(toIsoDate(new Date()))))} data-testid="week-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => moveWeek(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
          <Badge variant="outline" className={isPublished ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} data-testid="roster-status">
            {isPublished ? "PUBLISHED" : "DRAFT"}
          </Badge>
          <Button variant="outline" size="sm" onClick={copyLast} data-testid="copy-last-week"><Copy className="h-3.5 w-3.5 mr-1" /> Copy last week</Button>
          <TemplatesDialog currentWeek={weekMonday} onAppliedOrChanged={() => load(weekMonday)} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-[#C9A84C]/40 text-[#1A2744] hover:bg-[#FFF8E1]"
                data-testid="btn-share-roster"
                aria-label="Share roster"
              >
                <Share2 className="h-3.5 w-3.5 mr-1" /> Share
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-gray-500">
                Share this roster
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={sharePdf} data-testid="share-roster-pdf">
                <FileText className="h-4 w-4 mr-2 text-[#1A2744]" /> Share as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={sharePng} data-testid="share-roster-png">
                <FileImage className="h-4 w-4 mr-2 text-[#C9A84C]" /> Share as PNG
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-[10px] text-gray-500 leading-snug">
                On mobile this opens your share sheet (WhatsApp, iMessage, etc).
                On desktop the file downloads — drag it into any chat.
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {isPublished ? (
            <>
              <Button size="sm" onClick={notifyChanges} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="btn-notify-changes">
                <BellRing className="h-3.5 w-3.5 mr-1" /> Notify staff of changes
              </Button>
              <Button size="sm" variant="outline" onClick={unpublish} data-testid="btn-unpublish"><Undo2 className="h-3.5 w-3.5 mr-1" /> Unpublish</Button>
            </>
          ) : (
            <Button size="sm" onClick={publish} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="btn-publish"><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>
          )}
        </div>
      </header>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px]">
        {data.positions.map((p) => (
          <span key={p.code} className="inline-flex items-center gap-1.5 text-gray-600">
            <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: p.color }}></span>
            {POSITION_LABEL[p.code] || p.code}
          </span>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div ref={gridRef} className="min-w-[980px] bg-white">
            {/* Title strip — only rendered DURING the PNG snapshot so the
                shared image carries the cafe brand + week context. Toggled by
                `pngCapture` state for the brief moment toPng() runs. */}
            {pngCapture && (
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="text-xs uppercase tracking-widest text-[#C9A84C] font-semibold">Coffee Lovers Café · Roster</div>
                <div className="text-sm font-semibold text-[#1A2744] mt-0.5">
                  {fmtRange(weekMonday)} · {MANAGER_SITE_LABEL[data.site]} · {isPublished ? "Published" : "Draft"}
                </div>
              </div>
            )}
            {/* Header row */}
            <div className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-b border-gray-200 bg-gray-50">
              <div className="p-3 text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Employee</div>
              {days.map((d) => (
                <div key={d.iso} className="p-3 border-l border-gray-200 text-xs">
                  <div className="font-semibold text-[#1A2744]">{d.label}</div>
                  <div className="text-gray-500">{d.dom}</div>
                  <DayNotice notice={notices[d.iso]} iso={d.iso} />
                </div>
              ))}
            </div>

            {/* Employee rows */}
            {data.employees.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-500">No active employees at your site yet.</div>
            )}
            {data.employees.map((emp) => (
              <div key={emp.id} className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-b border-gray-100 hover:bg-[#FAFAF7]">
                <div className="p-3 min-w-0">
                  <div className="text-sm font-medium text-[#1A2744] truncate">{emp.name}</div>
                  <div className="text-[10px] text-gray-500">
                    <span className={emp.week_hours > emp.contracted_weekly ? "text-amber-700 font-semibold" : ""}>
                      {emp.week_hours}h
                    </span>
                    {" / "}
                    <span className="text-gray-400">{emp.contracted_weekly}h</span>
                  </div>
                </div>
                {days.map((d) => {
                  const cell = shiftsByCell[`${emp.id}|${d.iso}`] || [];
                  return (
                    <div key={d.iso} className="border-l border-gray-100 p-1.5 min-h-[68px] relative">
                      {cell.map((s) => (
                        <ShiftDialog
                          key={s.id}
                          data={s}
                          positions={data.positions}
                          employees={data.employees}
                          defaultDate={d.iso}
                          onSave={(p) => patchShift(s.id, p)}
                          onDelete={() => deleteShift(s.id)}
                          trigger={
                            <button
                              className="w-full mb-1 text-left rounded-md px-2 py-1.5 text-[11px] text-white font-medium hover:opacity-90"
                              style={{ backgroundColor: positionColor(s.position) }}
                              data-testid={`shift-${s.id}`}
                            >
                              <div className="font-semibold">{s.start_time}–{s.end_time}</div>
                              <div className="opacity-90 text-[10px] truncate">{POSITION_LABEL[s.position] || s.position}</div>
                            </button>
                          }
                        />
                      ))}
                      <ShiftDialog
                        positions={data.positions}
                        employees={data.employees}
                        defaultDate={d.iso}
                        onSave={(p) => addShift({ ...p, user_id: emp.id })}
                        trigger={
                          <button
                            className="w-full rounded-md py-1 text-[10px] text-gray-400 hover:text-[#1A2744] hover:bg-[#C9A84C]/10 border border-dashed border-gray-200"
                            data-testid={`add-${emp.id}-${d.iso}`}
                          >
                            <Plus className="h-3 w-3 inline" /> add
                          </button>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Unassigned shifts row (from templates) */}
            {data.shifts.some((s) => s.user_id === "__unassigned__") && (
              <div className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-t-2 border-dashed border-[#C9A84C]/40 bg-[#FFFBF0]">
                <div className="p-3 text-xs font-semibold text-[#C9A84C] flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Unassigned
                </div>
                {days.map((d) => {
                  const cell = (data.shifts || []).filter((s) => s.user_id === "__unassigned__" && s.date === d.iso);
                  return (
                    <div key={d.iso} className="border-l border-gray-100 p-1.5 min-h-[68px]">
                      {cell.map((s) => (
                        <ShiftDialog
                          key={s.id}
                          data={s}
                          positions={data.positions}
                          employees={data.employees}
                          defaultDate={d.iso}
                          onSave={(p) => patchShift(s.id, p)}
                          onDelete={() => deleteShift(s.id)}
                          trigger={
                            <button
                              className="w-full mb-1 text-left rounded-md px-2 py-1.5 text-[11px] font-medium hover:opacity-90 border border-dashed"
                              style={{ borderColor: positionColor(s.position), color: positionColor(s.position) }}
                              data-testid={`shift-unassigned-${s.id}`}
                            >
                              <div className="font-semibold">{s.start_time}–{s.end_time}</div>
                              <div className="text-[10px] truncate">Assign →</div>
                            </button>
                          }
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <p className="text-[11px] text-gray-400">
        Published rosters are visible to staff. Draft rosters stay private. Changes to a published roster notify the affected employee automatically.
      </p>
    </div>
  );
}
